/**
 * Fetch wrapper com CSRF auto-inject + 401 handling.
 * Porte de backend/static/js/api.js para TypeScript.
 *
 * Base URL resolvido via VITE_API_URL (default: "/api", ou seja, mesmo host).
 * Requisições com cookie de sessão Django passam via `credentials: "include"`.
 */
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function readCookie(name: string): string {
  const match = document.cookie.match(new RegExp("(^|;)\\s*" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : "";
}

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(status: number, message: string, data?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

function extractMessage(data: unknown): string | null {
  if (!data) return null;
  if (typeof data === "string") return data;
  if (typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (typeof obj.detail === "string") return obj.detail;
    if (typeof obj.message === "string") return obj.message;
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      if (Array.isArray(v) && v.length) return `${k}: ${String(v[0])}`;
      if (typeof v === "string") return `${k}: ${v}`;
    }
  }
  return null;
}

function resolveUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith("/api/")) {
    // Caller passou path absoluto /api/...; mantém.
    return path;
  }
  if (path.startsWith("/")) return path;
  return `${API_BASE.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

export interface RequestOptions {
  headers?: Record<string, string>;
  body?: unknown;
}

async function request<T = unknown>(
  method: string,
  url: string,
  options: RequestOptions = {},
): Promise<T> {
  const headers = new Headers(options.headers || {});
  headers.set("Accept", "application/json");

  let body: BodyInit | undefined;
  const raw = options.body;
  if (raw != null) {
    if (raw instanceof FormData || raw instanceof URLSearchParams) {
      body = raw;
    } else if (typeof raw === "object") {
      headers.set("Content-Type", "application/json");
      body = JSON.stringify(raw);
    } else {
      body = String(raw);
    }
  }

  if (UNSAFE.has(method)) {
    const csrf = readCookie("csrftoken");
    if (csrf) headers.set("X-CSRFToken", csrf);
  }

  // Simulação de CPF (superadmin/admin): envia header em TODAS as requisições
  // quando sessionStorage tiver um CPF de simulação ativo.
  // Segurança: backend só honra o header para usuários com permissão de override.
  const simCpf = sessionStorage.getItem("portal_sim_cpf");
  if (simCpf) headers.set("X-Sim-Cpf", simCpf);

  const resp = await fetch(resolveUrl(url), {
    method,
    headers,
    body,
    credentials: "include",
  });

  if (resp.status === 401) {
    window.location.href = "/login/?next=" + encodeURIComponent(window.location.pathname);
    throw new ApiError(resp.status, "Sessão expirada.");
  }

  const isJson = (resp.headers.get("content-type") || "").includes("application/json");
  const data: unknown = isJson ? await resp.json().catch(() => null) : await resp.text();

  if (!resp.ok) {
    throw new ApiError(resp.status, extractMessage(data) || `HTTP ${resp.status}`, data);
  }
  return data as T;
}

export const api = {
  get:   <T = unknown>(url: string, opts?: RequestOptions) => request<T>("GET", url, opts),
  post:  <T = unknown>(url: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("POST", url, { ...opts, body }),
  put:   <T = unknown>(url: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("PUT", url, { ...opts, body }),
  patch: <T = unknown>(url: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("PATCH", url, { ...opts, body }),
  del:   <T = unknown>(url: string, opts?: RequestOptions) => request<T>("DELETE", url, opts),
  // POST que retorna um arquivo (blob) — para download (ex.: termos, documentos).
  postBlob: async (url: string, body?: unknown): Promise<Blob> => {
    const headers = new Headers({ "Content-Type": "application/json" });
    const csrf = readCookie("csrftoken");
    if (csrf) headers.set("X-CSRFToken", csrf);
    const simCpf = sessionStorage.getItem("portal_sim_cpf");
    if (simCpf) headers.set("X-Sim-Cpf", simCpf);
    const resp = await fetch(resolveUrl(url), {
      method: "POST", headers, credentials: "include",
      body: body != null ? JSON.stringify(body) : undefined,
    });
    if (resp.status === 401) {
      window.location.href = "/login/?next=" + encodeURIComponent(window.location.pathname);
      throw new ApiError(resp.status, "Sessão expirada.");
    }
    if (!resp.ok) {
      const txt = await resp.text().catch(() => "");
      throw new ApiError(resp.status, txt || `HTTP ${resp.status}`);
    }
    return resp.blob();
  },
};
